import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPendingDevices, getAuthorizedDevices, getBlockedDevices, authorizeDevice, revokeDevice, blockDevice, unblockDevice, deleteDevice, deleteDevicesByStore, deleteDevicesNoStore, deleteAllDevicesForUser, getStores, getUsers } from '../api/api';
import { Smartphone, Clock, CheckCircle, XCircle, Shield, Trash2, Store, Users, User } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const Devices = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingDevices, setPendingDevices] = useState([]);
  const [authorizedDevices, setAuthorizedDevices] = useState([]);
  const [blockedDevices, setBlockedDevices] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedStoreForDelete, setSelectedStoreForDelete] = useState('');
  const [selectedUserForDelete, setSelectedUserForDelete] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load stores and users for bulk delete
  useEffect(() => {
    getStores().then(setStores).catch(console.error);
    getUsers().then(setUsers).catch(console.error);
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'pending') {
        const data = await getPendingDevices();
        setPendingDevices(data);
      } else if (activeTab === 'authorized') {
        const data = await getAuthorizedDevices();
        setAuthorizedDevices(data);
      } else if (activeTab === 'blocked') {
        const data = await getBlockedDevices();
        setBlockedDevices(data);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Error al cargar dispositivos');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleAuthorize = async (deviceId) => {
    if (!window.confirm('¿Está seguro de autorizar este dispositivo?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      await authorizeDevice(deviceId);
      setSuccess('¡Dispositivo autorizado exitosamente!');
      await loadDevices();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error authorizing device:', error);
      setError(error.response?.data?.detail || 'Error al autorizar dispositivo');
    }
  };

  const handleRevoke = async (deviceId) => {
    if (!window.confirm('¿Está seguro de revocar este dispositivo? El usuario podrá volver a solicitar acceso.')) return;
    
    setError('');
    setSuccess('');
    
    try {
      await revokeDevice(deviceId);
      setSuccess('¡Dispositivo revocado exitosamente!');
      await loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al revocar dispositivo');
    }
  };

  const handleBlock = async (deviceId) => {
    if (!window.confirm('⚠️ ¿Está seguro de BLOQUEAR PERMANENTEMENTE este dispositivo? No podrá volver a solicitar acceso.')) return;
    
    setError('');
    setSuccess('');
    
    try {
      await blockDevice(deviceId);
      setSuccess('¡Dispositivo bloqueado permanentemente!');
      await loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al bloquear dispositivo');
    }
  };

  const handleUnblock = async (deviceId) => {
    if (!window.confirm('¿Está seguro de desbloquear este dispositivo?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      await unblockDevice(deviceId);
      setSuccess('¡Dispositivo desbloqueado exitosamente!');
      await loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al desbloquear dispositivo');
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm('¿Está seguro de eliminar este registro? Esta acción no se puede deshacer.')) return;
    
    setError('');
    setSuccess('');
    
    try {
      await deleteDevice(deviceId);
      setSuccess('¡Registro eliminado exitosamente!');
      await loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar dispositivo');
    }
  };

  const handleDeleteByStore = async () => {
    if (!selectedStoreForDelete) {
      setError('Selecciona una tienda primero');
      return;
    }
    
    const storeName = stores.find(s => s.id === selectedStoreForDelete)?.name || 'la tienda';
    if (!window.confirm(`⚠️ ¿Está seguro de ELIMINAR TODOS los dispositivos de "${storeName}"?\n\nEsta acción no se puede deshacer y los usuarios tendrán que volver a solicitar acceso.`)) return;
    
    setError('');
    setSuccess('');
    
    try {
      const result = await deleteDevicesByStore(selectedStoreForDelete);
      setSuccess(`${result.message} (${result.deleted_count} dispositivos eliminados)`);
      setSelectedStoreForDelete('');
      await loadDevices();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar dispositivos');
    }
  };

  const handleDeleteNoStore = async () => {
    if (!window.confirm(`⚠️ ¿Está seguro de ELIMINAR TODOS los dispositivos de usuarios SIN TIENDA?\n\n(Esto incluye: admin, gerencia, supervisores, auxiliares, etc.)\n\nEsta acción no se puede deshacer.`)) return;
    
    setError('');
    setSuccess('');
    
    try {
      const result = await deleteDevicesNoStore();
      const usersAffected = result.users_affected?.join(', ') || '';
      setSuccess(`${result.message} (${result.deleted_count} dispositivos eliminados) - Usuarios: ${usersAffected}`);
      await loadDevices();
      setTimeout(() => setSuccess(''), 7000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar dispositivos');
    }
  };

  const handleDeleteByUser = async () => {
    if (!selectedUserForDelete) {
      setError('Selecciona un usuario primero');
      return;
    }
    
    const userName = users.find(u => u.id === selectedUserForDelete)?.name || 'el usuario';
    const userUsername = users.find(u => u.id === selectedUserForDelete)?.username || '';
    if (!window.confirm(`⚠️ ¿Eliminar TODOS los dispositivos de "${userName}" (${userUsername})?\n\nEl usuario tendrá que volver a solicitar acceso.`)) return;
    
    setError('');
    setSuccess('');
    
    try {
      const result = await deleteAllDevicesForUser(selectedUserForDelete);
      setSuccess(`${result.message} (${result.deleted_count} dispositivos eliminados)`);
      setSelectedUserForDelete('');
      await loadDevices();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar dispositivos');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-800 text-lg">No tienes permisos para acceder a esta sección</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
          <Smartphone size={32} />
          <span>Gestión de Dispositivos</span>
        </h1>
        <p className="text-gray-600 mt-1">Autorizar y administrar dispositivos del sistema</p>
      </div>

      {error && (
        <div data-testid="devices-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div data-testid="devices-success" className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-2">🔐 Sistema de Seguridad por Dispositivo</h2>
        <p className="text-blue-50">
          Solo los dispositivos autorizados pueden acceder al sistema. Revisa las solicitudes pendientes
          y aprueba los dispositivos de confianza.
        </p>
      </div>

      {/* Bulk Delete Section */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center space-x-2">
          <Trash2 size={20} className="text-red-600" />
          <span>Gestión de Dispositivos</span>
        </h3>
        
        {/* Por usuario específico - OPCIÓN PRINCIPAL */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center space-x-2">
            <User size={16} />
            <span>Eliminar dispositivos de UN usuario específico (Recomendado)</span>
          </h4>
          <div className="flex flex-wrap items-center gap-3">
            <select
              data-testid="user-select-for-delete"
              value={selectedUserForDelete}
              onChange={(e) => setSelectedUserForDelete(e.target.value)}
              className="flex-1 min-w-[250px] px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Seleccionar usuario...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.username}) - {u.role}
                </option>
              ))}
            </select>
            <button
              data-testid="delete-by-user-button"
              onClick={handleDeleteByUser}
              disabled={!selectedUserForDelete}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Trash2 size={18} />
              <span>Eliminar dispositivos</span>
            </button>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            ✓ Solo afecta al usuario seleccionado. Podrá volver a solicitar acceso después.
          </p>
        </div>

        {/* Opciones masivas (colapsadas por defecto) */}
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-gray-600 hover:bg-gray-50">
            ⚠️ Opciones de eliminación masiva (usar con precaución)
          </summary>
          <div className="p-4 space-y-4 bg-gray-50">
            {/* Por tienda */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2 min-w-[150px]">
                <Store size={16} className="text-red-600" />
                <span className="text-sm text-gray-700">Por tienda:</span>
              </div>
              <select
                data-testid="store-select-for-delete"
                value={selectedStoreForDelete}
                onChange={(e) => setSelectedStoreForDelete(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Seleccionar tienda...</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
              <button
                data-testid="delete-by-store-button"
                onClick={handleDeleteByStore}
                disabled={!selectedStoreForDelete}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm flex items-center space-x-1"
              >
                <Trash2 size={14} />
                <span>Eliminar</span>
              </button>
            </div>

            {/* Usuarios sin tienda */}
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-300">
              <div className="flex items-center space-x-2 flex-1">
                <Users size={16} className="text-orange-600" />
                <span className="text-sm text-gray-700">Usuarios sin tienda (admin, supervisores, etc.):</span>
              </div>
              <button
                data-testid="delete-no-store-button"
                onClick={handleDeleteNoStore}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex items-center space-x-1"
              >
                <Trash2 size={14} />
                <span>Eliminar todos</span>
              </button>
            </div>
          </div>
        </details>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex border-b">
          <button
            data-testid="tab-pending"
            onClick={() => setActiveTab('pending')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'pending'
                ? 'bg-yellow-50 text-yellow-700 border-b-2 border-yellow-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Clock size={20} />
              <span>Pendientes</span>
              {pendingDevices.length > 0 && (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                  {pendingDevices.length}
                </span>
              )}
            </div>
          </button>

          <button
            data-testid="tab-authorized"
            onClick={() => setActiveTab('authorized')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'authorized'
                ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle size={20} />
              <span>Autorizados</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('blocked')}
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'blocked'
                ? 'bg-red-50 text-red-700 border-b-2 border-red-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <XCircle size={20} />
              <span>Bloqueados</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-600">Cargando...</div>
          ) : (
            <>
              {/* Pending Devices */}
              {activeTab === 'pending' && (
                <div className="space-y-4">
                  {pendingDevices.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="mx-auto text-gray-400 mb-4" size={48} />
                      <p className="text-gray-600">No hay dispositivos pendientes</p>
                    </div>
                  ) : (
                    pendingDevices.map((device) => (
                      <div key={device.id} className="border border-yellow-200 bg-yellow-50 rounded-lg p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <Smartphone className="text-yellow-600" size={24} />
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{device.device_name}</h3>
                                <p className="text-sm text-gray-600">Usuario: <strong>{device.username}</strong></p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600">Navegador:</p>
                                <p className="font-medium">{device.device_info?.browserName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Sistema:</p>
                                <p className="font-medium">{device.device_info?.osName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Primera vez visto:</p>
                                <p className="font-medium">{formatDate(device.first_seen)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Resolución:</p>
                                <p className="font-medium">{device.device_info?.screenResolution || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Zona Horaria:</p>
                                <p className="font-medium">{device.device_info?.timezone || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">IP:</p>
                                <p className="font-medium">{device.ip_address || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-3 mt-4">
                          <button
                            onClick={() => handleAuthorize(device.id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                          >
                            <CheckCircle size={18} />
                            <span>Aprobar</span>
                          </button>
                          <button
                            onClick={() => handleBlock(device.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                          >
                            <XCircle size={18} />
                            <span>Bloquear Permanente</span>
                          </button>
                          <button
                            onClick={() => handleDelete(device.id)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                          >
                            <Trash2 size={18} />
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Authorized Devices */}
              {activeTab === 'authorized' && (
                <div className="space-y-4">
                  {authorizedDevices.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="mx-auto text-gray-400 mb-4" size={48} />
                      <p className="text-gray-600">No hay dispositivos autorizados</p>
                    </div>
                  ) : (
                    authorizedDevices.map((device) => (
                      <div key={device.id} className="border border-green-200 bg-green-50 rounded-lg p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <Smartphone className="text-green-600" size={24} />
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{device.device_name}</h3>
                                <p className="text-sm text-gray-600">Usuario: <strong>{device.username}</strong></p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600">Navegador:</p>
                                <p className="font-medium">{device.device_info?.browserName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Sistema:</p>
                                <p className="font-medium">{device.device_info?.osName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Autorizado:</p>
                                <p className="font-medium">{formatDate(device.authorized_at)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Último uso:</p>
                                <p className="font-medium">{formatDate(device.last_used)}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Autorizado por:</p>
                                <p className="font-medium">{device.authorized_by_name || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-3 mt-4">
                          <button
                            onClick={() => handleRevoke(device.id)}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                          >
                            <XCircle size={18} />
                            <span>Revocar</span>
                          </button>
                          <button
                            onClick={() => handleBlock(device.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                          >
                            <Shield size={18} />
                            <span>Bloquear</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Blocked Devices */}
              {activeTab === 'blocked' && (
                <div className="space-y-4">
                  {blockedDevices.length === 0 ? (
                    <div className="text-center py-12">
                      <XCircle className="mx-auto text-gray-400 mb-4" size={48} />
                      <p className="text-gray-600">No hay dispositivos bloqueados</p>
                    </div>
                  ) : (
                    blockedDevices.map((device) => (
                      <div key={device.id} className="border border-red-200 bg-red-50 rounded-lg p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <Smartphone className="text-red-600" size={24} />
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{device.device_name}</h3>
                                <p className="text-sm text-gray-600">Usuario: <strong>{device.username}</strong></p>
                                <span className="inline-block mt-1 px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded-full">
                                  BLOQUEADO PERMANENTE
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600">Navegador:</p>
                                <p className="font-medium">{device.device_info?.browserName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Sistema:</p>
                                <p className="font-medium">{device.device_info?.osName || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Primera vez visto:</p>
                                <p className="font-medium">{formatDate(device.first_seen)}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-3 mt-4">
                          <button
                            onClick={() => handleUnblock(device.id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                          >
                            <CheckCircle size={18} />
                            <span>Desbloquear</span>
                          </button>
                          <button
                            onClick={() => handleDelete(device.id)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                          >
                            <Trash2 size={18} />
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3">ℹ️ Información</h3>
        <ul className="space-y-2 text-blue-800 text-sm">
          <li><strong>Aprobar:</strong> Permite al dispositivo acceder al sistema</li>
          <li><strong>Revocar:</strong> Bloquea temporalmente. El usuario puede volver a solicitar</li>
          <li><strong>Bloquear Permanente:</strong> El dispositivo nunca podrá volver a solicitar acceso</li>
          <li><strong>Desbloquear:</strong> Permite que un dispositivo bloqueado vuelva a solicitar</li>
          <li><strong>Eliminar:</strong> Borra el registro completamente del sistema</li>
        </ul>
      </div>
    </div>
  );
};

export default Devices;
